import { createContext, useContext, useState, useEffect, useRef, type ReactNode } from "react";
import type { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabase";

interface AuthState {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isAdmin: boolean;
  isAgency: boolean;
  roleLoading: boolean;
  perMessageRate: number | null;
  sessionConflict: boolean;
  pendingBlocked: boolean;
}

interface AuthContextType extends AuthState {
  signIn: (email: string, password: string) => Promise<{ data: any; error: any }>;
  signUp: (
    email: string,
    password: string,
    meta: { name: string; phone: string; referralCode?: string }
  ) => Promise<{ data: any; error: any }>;
  signOut: () => Promise<{ error: any }>;
  resetPassword: (email: string) => Promise<{ data: any; error: any }>;
}

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
const SUPABASE_ANON_KEY = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string;

interface RoleResponse {
  role?: string;
  is_agency?: boolean;
  per_message_rate?: number | null;
  status?: string;
  error?: string;
}

function generateSessionId(): string {
  return crypto.randomUUID();
}

// --- 인증 스냅샷 캐시 ---
// 브라우저가 백그라운드 탭을 리로드해도 로딩 화면 없이 즉시 화면을 그릴 수 있도록
// 최소 인증 정보를 로컬에 캐시한다. (권한 정보는 백그라운드에서 조용히 재검증)
const AUTH_SNAPSHOT_KEY = "auth_snapshot_v1";

interface AuthSnapshot {
  id: string;
  email: string | null;
  full_name?: string;
  isAdmin: boolean;
  isAgency: boolean;
  perMessageRate: number | null;
}

function readAuthSnapshot(): AuthSnapshot | null {
  try {
    const raw = localStorage.getItem(AUTH_SNAPSHOT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as AuthSnapshot | null;
    if (!parsed || typeof parsed.id !== "string" || !parsed.id) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeAuthSnapshot(snapshot: AuthSnapshot): void {
  try {
    localStorage.setItem(AUTH_SNAPSHOT_KEY, JSON.stringify({ ...snapshot, ts: Date.now() }));
  } catch {
    // 저장 실패(용량 초과 등)는 무시
  }
}

function clearAuthSnapshot(): void {
  try {
    localStorage.removeItem(AUTH_SNAPSHOT_KEY);
  } catch {
    // 무시
  }
}

function snapshotToUser(snapshot: AuthSnapshot): User {
  return {
    id: snapshot.id,
    email: snapshot.email ?? undefined,
    user_metadata: { full_name: snapshot.full_name },
  } as unknown as User;
}

async function fetchRoleViaEdge(accessToken: string): Promise<{ ok: boolean; isAdmin: boolean; isAgency: boolean; perMessageRate: number | null; status: string }> {
  try {
    const res = await fetch(`${SUPABASE_URL}/functions/v1/get-user-role`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${accessToken}`,
      },
    });
    if (!res.ok) return { ok: false, isAdmin: false, isAgency: false, perMessageRate: null, status: "active" };
    const data = (await res.json()) as RoleResponse;
    // 역할 조회 실패(에러/role 누락)는 ok=false로 처리해 기존 권한을 강등하지 않는다.
    if (!data || data.error || !data.role) {
      return { ok: false, isAdmin: false, isAgency: false, perMessageRate: null, status: "active" };
    }
    return {
      ok: true,
      isAdmin: data.role === "admin",
      isAgency: data?.is_agency === true,
      perMessageRate: data?.per_message_rate ?? null,
      status: data?.status ?? "active",
    };
  } catch {
    return { ok: false, isAdmin: false, isAgency: false, perMessageRate: null, status: "active" };
  }
}

async function waitForProfile(userId: string, timeoutMs: number): Promise<boolean> {
  const start = Date.now();
  while (Date.now() - start < timeoutMs) {
    const { data } = await supabase.from("profiles").select("id").eq("id", userId).maybeSingle();
    if (data) return true;
    await new Promise((r) => setTimeout(r, 300));
  }
  return false;
}

async function checkSessionConflict(userId: string): Promise<boolean> {
  const localSessionId = localStorage.getItem("active_session_id");
  if (!localSessionId) return false;

  try {
    const { data } = await supabase
      .from("profiles")
      .select("active_session_id")
      .eq("id", userId)
      .maybeSingle();

    if (
      data?.active_session_id &&
      data.active_session_id !== localSessionId
    ) {
      return true;
    }
  } catch {
    // 네트워크 오류 시 충돌로 간주하지 않음
  }
  return false;
}

const AuthContext = createContext<AuthContextType | null>(null);

function isRefreshTokenError(err: any): boolean {
  if (!err) return false;
  const msg = err.message || err.error_description || "";
  return (
    msg.includes("Invalid Refresh Token") ||
    msg.includes("Refresh Token Not Found") ||
    msg.includes("invalid refresh token") ||
    msg.includes("token_not_found") ||
    msg.includes("Invalid JWT") ||
    msg.includes("invalid jwt")
  );
}

function clearAuthState() {
  try {
    const keys = Object.keys(localStorage);
    for (const key of keys) {
      if (key.startsWith("sb-") || key.startsWith("supabase")) {
        localStorage.removeItem(key);
      }
    }
  } catch {
    // ignore
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const initialSnapshotRef = useRef<AuthSnapshot | null>(readAuthSnapshot());

  const [state, setState] = useState<AuthState>(() => {
    const snap = initialSnapshotRef.current;
    if (snap) {
      // 캐시가 있으면 로딩 화면 없이 즉시 렌더 (백그라운드 재검증)
      return {
        user: snapshotToUser(snap),
        session: null,
        loading: false,
        isAdmin: snap.isAdmin,
        isAgency: snap.isAgency,
        roleLoading: false,
        perMessageRate: snap.perMessageRate,
        sessionConflict: false,
        pendingBlocked: false,
      };
    }
    return {
      user: null,
      session: null,
      loading: true,
      isAdmin: false,
      isAgency: false,
      roleLoading: true,
      perMessageRate: null,
      sessionConflict: false,
      pendingBlocked: false,
    };
  });

  const resolvedRef = useRef(false);
  // 캐시로 이미 화면을 그렸는지 여부 (리로드 후 조용한 재검증 모드)
  const hydratedRef = useRef(initialSnapshotRef.current !== null);
  const conflictCheckIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const persistSnapshot = (
    session: Session,
    isAdmin: boolean,
    isAgency: boolean,
    perMessageRate: number | null
  ) => {
    writeAuthSnapshot({
      id: session.user.id,
      email: session.user.email ?? null,
      full_name: (session.user.user_metadata?.full_name as string | undefined) ?? undefined,
      isAdmin,
      isAgency,
      perMessageRate,
    });
  };

  const setLoggedOut = () => {
    resolvedRef.current = true;
    clearAuthSnapshot();
    setState((prev) => ({
      ...prev,
      user: null,
      session: null,
      loading: false,
      isAdmin: false,
      isAgency: false,
      roleLoading: false,
      perMessageRate: null,
    }));
  };

  const setPendingBlocked = () => {
    resolvedRef.current = true;
    clearAuthSnapshot();
    setState({
      user: null,
      session: null,
      loading: false,
      isAdmin: false,
      isAgency: false,
      roleLoading: false,
      perMessageRate: null,
      sessionConflict: false,
      pendingBlocked: true,
    });
  };

  const handleSession = (session: Session | null, skipConflictCheck: boolean = false) => {
    if (!session) {
      setLoggedOut();
      return;
    }

    // 캐시로 이미 화면을 그린 경우: 로딩 화면 없이 조용히 재검증 (silent)
    const silent = hydratedRef.current;
    hydratedRef.current = true;

    setState((prev) => ({
      ...prev,
      user: session.user,
      session,
      loading: false,
      roleLoading: silent ? false : true,
      sessionConflict: false,
      pendingBlocked: false,
    }));

    // 역할 정보 가져오기
    fetchRoleViaEdge(session.access_token).then(({ ok, isAdmin, isAgency, perMessageRate, status }) => {
      // 관리자 승인 대기 중인 경우 (단, admin은 통과)
      if (status === "pending" && !isAdmin) {
        localStorage.removeItem("active_session_id");
        clearAuthSnapshot();
        supabase.auth.signOut({ scope: "local" }).finally(() => {
          clearAuthState();
          setPendingBlocked();
        });
        return;
      }

      // 역할 조회 실패: 기존 권한을 그대로 유지한다.
      // (조회 실패를 "일반회원"으로 간주해 관리자가 강등/캐시되는 것을 방지)
      if (!ok) {
        resolvedRef.current = true;
        setState((prev) => ({
          ...prev,
          user: session.user,
          session,
          loading: false,
          roleLoading: false,
          sessionConflict: false,
          pendingBlocked: false,
        }));
        return;
      }

      // 충돌 체크 (SIGNED_IN 이벤트 시에는 스킵)
      if (skipConflictCheck) {
        resolvedRef.current = true;
        setState({
          user: session.user,
          session,
          loading: false,
          isAdmin,
          isAgency,
          roleLoading: false,
          perMessageRate,
          sessionConflict: false,
          pendingBlocked: false,
        });
        persistSnapshot(session, isAdmin, isAgency, perMessageRate);
        return;
      }

      checkSessionConflict(session.user.id).then((conflict) => {
        if (conflict) {
          localStorage.removeItem("active_session_id");
          clearAuthSnapshot();
          supabase.auth.signOut({ scope: "local" }).finally(() => {
            clearAuthState();
            resolvedRef.current = true;
            setState({
              user: null,
              session: null,
              loading: false,
              isAdmin: false,
              isAgency: false,
              roleLoading: false,
              perMessageRate: null,
              sessionConflict: true,
              pendingBlocked: false,
            });
          });
          return;
        }

        resolvedRef.current = true;
        setState({
          user: session.user,
          session,
          loading: false,
          isAdmin,
          isAgency,
          roleLoading: false,
          perMessageRate,
          sessionConflict: false,
          pendingBlocked: false,
        });
        persistSnapshot(session, isAdmin, isAgency, perMessageRate);
      });
    }).catch(() => {
      resolvedRef.current = true;
      setState((prev) => ({
        ...prev,
        loading: false,
        roleLoading: false,
        sessionConflict: false,
        pendingBlocked: false,
      }));
    });
  };

  useEffect(() => {
    let mounted = true;

    // ⚠️ onAuthStateChange 콜백은 반드시 동기(synchronous)로 유지해야 함
    // async/await를 사용하면 SDK 내부 락 컨텍스트와 충돌하여 데드락/리프레시 에러가 발생함
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (!mounted) return;

        // SIGNED_OUT 이벤트: 리프레시 토큰 만료 등으로 SDK가 자동 로그아웃 처리한 경우
        if (event === "SIGNED_OUT") {
          clearAuthState();
          setLoggedOut();
          return;
        }

        // USER_DELETED 이벤트: 사용자 계정 삭제됨
        if (event === "USER_DELETED") {
          clearAuthState();
          setLoggedOut();
          return;
        }

        // TOKEN_REFRESHED 이벤트: 세션 갱신 성공 - 역할 정보는 그대로 유지
        if (event === "TOKEN_REFRESHED") {
          if (!session) {
            setLoggedOut();
            return;
          }
          setState((prev) => ({
            ...prev,
            user: session.user,
            session,
            loading: false,
            sessionConflict: false,
          }));
          return;
        }

        // SIGNED_IN 이벤트일 때는 방금 로그인한 거라 충돌 체크 스킵
        handleSession(session, event === "SIGNED_IN");
      }
    );

    // getSession() 호출 시 리프레시 토큰 에러를 조용히 처리
    // 페이지 새로고침 시에는 충돌 체크 필요 (SIGNED_IN 이벤트가 아님)
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!mounted) return;
      if (!resolvedRef.current) {
        handleSession(session, false);
      }
    }).catch((err: any) => {
      if (!mounted) return;
      if (isRefreshTokenError(err)) {
        supabase.auth.signOut({ scope: "local" }).finally(() => {
          clearAuthState();
          setLoggedOut();
        });
      } else {
        setLoggedOut();
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  // --- 주기적 중복 로그인 폴링 (15초마다) ---
  useEffect(() => {
    // 로그인된 상태일 때만 폴링 시작
    if (state.user && state.session && !state.loading && !state.roleLoading) {
      const localSessionId = localStorage.getItem("active_session_id");
      if (!localSessionId) return;

      conflictCheckIntervalRef.current = setInterval(async () => {
        const conflict = await checkSessionConflict(state.user!.id);
        if (conflict) {
          // 충돌 감지! 강제 로그아웃
          localStorage.removeItem("active_session_id");

          // 인터벌 정리
          if (conflictCheckIntervalRef.current) {
            clearInterval(conflictCheckIntervalRef.current);
            conflictCheckIntervalRef.current = null;
          }

          supabase.auth.signOut({ scope: "local" }).finally(() => {
            clearAuthState();
            setState({
              user: null,
              session: null,
              loading: false,
              isAdmin: false,
              isAgency: false,
              roleLoading: false,
              perMessageRate: null,
              sessionConflict: true,
              pendingBlocked: false,
            });
          });
        }
      }, 15000); // 15초마다 체크
    }

    return () => {
      if (conflictCheckIntervalRef.current) {
        clearInterval(conflictCheckIntervalRef.current);
        conflictCheckIntervalRef.current = null;
      }
    };
  }, [state.user?.id, state.loading, state.roleLoading]);

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });

    if (!error && data.session?.user) {
      const sessionId = generateSessionId();
      localStorage.setItem("active_session_id", sessionId);

      // 프로필 업데이트 (onAuthStateChange에서 SIGNED_IN 이벤트가 발생하기 전에 완료됨)
      supabase.from("profiles")
        .update({ active_session_id: sessionId })
        .eq("id", data.session.user.id)
        .then(() => {});
    }

    return { data, error };
  };

  const signUp = async (
    email: string,
    password: string,
    meta: { name: string; phone: string; referralCode?: string }
  ) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: meta.name,
          phone: meta.phone,
        },
      },
    });
    if (error || !data.user) return { data, error };

    // 프로필 생성 대기 후 status를 pending으로 설정
    const found = await waitForProfile(data.user.id, 5000);
    if (found) {
      await supabase.from("profiles").update({ status: "pending" }).eq("id", data.user.id);
    }

    // 세션이 생성된 경우(이메일 인증 없이 바로 로그인) → pending이므로 강제 로그아웃
    if (data.session?.user) {
      localStorage.removeItem("active_session_id");
      await supabase.auth.signOut({ scope: "local" });
      clearAuthState();
    }

    if (meta.referralCode) {
      try {
        const res = await fetch(`${SUPABASE_URL}/functions/v1/get-agency-by-code`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            apikey: SUPABASE_ANON_KEY,
            Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({ code: meta.referralCode }),
        });
        if (res.ok) {
          const agency = await res.json();
          await supabase.from("profiles").update({ parent_agency_id: agency.id }).eq("id", data.user.id);
        }
      } catch (e) {
        console.error("Referral code processing failed:", e);
      }
    }

    // 텔레그램 회원가입 알림 (fire-and-forget)
    const notifyUrl = SUPABASE_URL.replace(".supabase.co", ".helloreaddy.com") + "/functions/v1/notify-telegram";
    fetch(notifyUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        type: "signup",
        title: "신규 회원가입",
        body: `이름: ${meta.name}\n이메일: ${email}\n전화번호: ${meta.phone}\n추천인: ${meta.referralCode || "없음"}\n시간: ${new Date().toLocaleString("ko-KR")}`,
      }),
    }).catch(() => {});

    return { data, error };
  };

  const signOut = async () => {
    // 폴링 인터벌 정리
    if (conflictCheckIntervalRef.current) {
      clearInterval(conflictCheckIntervalRef.current);
      conflictCheckIntervalRef.current = null;
    }
    localStorage.removeItem("active_session_id");
    setLoggedOut();
    const { error } = await supabase.auth.signOut();
    return { error };
  };

  const resetPassword = async (email: string) => {
    const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    return { data, error };
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        signIn,
        signUp,
        signOut,
        resetPassword,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return ctx;
}